import { logger } from "../../utils/logger";
import type { BodyUUID, HingeJoint, Joint, SlideJoint } from "../../types/scene";
import type { ExportBodyNode } from "./xmlGenerationTypes";

type KeyframeDetail = {
  ctrl?: unknown;
  mpos?: unknown;
  mquat?: unknown;
  act?: unknown;
};

export const emitKeyframeXml = ({
  initQpos,
  storedKeyframes,
  keyframeDetails,
  skipKeyframeNames,
  reserveUniqueName,
  arrayToString,
  xmlEscapeAttr,
  addExportWarning,
}: {
  initQpos: number[];
  storedKeyframes: Record<string, unknown>;
  keyframeDetails: Record<string, KeyframeDetail | undefined>;
  skipKeyframeNames: Set<string>;
  reserveUniqueName: (
    preferredRaw: string | undefined | null,
    fallbackRaw: string,
    used: Set<string>
  ) => string;
  arrayToString: (values: Array<number | string>) => string;
  xmlEscapeAttr: (value: string) => string;
  addExportWarning: (message: string) => void;
}): string => {
  const keyframeLines: string[] = [];
  const usedKeyframeNames = new Set<string>();
  const getKeyframeDetail = (rawName: string) => {
    const direct = keyframeDetails[rawName];
    if (direct) return direct;
    const separator = " :: ";
    const separatorIndex = rawName.indexOf(separator);
    if (separatorIndex < 0) return undefined;
    return keyframeDetails[rawName.slice(separatorIndex + separator.length)];
  };
  const appendKeyframe = (
    rawName: string,
    rawQpos: unknown,
    options?: { warnOnLengthMismatch?: boolean }
  ) => {
    if (options?.warnOnLengthMismatch && skipKeyframeNames.has(rawName)) return;
    if (!Array.isArray(rawQpos)) return;
    const qpos = rawQpos.map((value) => Number(value));
    if (qpos.some((value) => !Number.isFinite(value))) {
      logger.warn(
        `[xmlGenerationActions] Skipping keyframe "${rawName}" because qpos contains non-finite values.`
      );
      return;
    }
    if (initQpos.length > 0 && qpos.length !== initQpos.length) {
      if (options?.warnOnLengthMismatch) {
        logger.warn(
          `[xmlGenerationActions] Skipping keyframe "${rawName}" because qpos length ${qpos.length} does not match generated model nq ${initQpos.length}.`
        );
        addExportWarning(
          `Keyframe "${rawName}" omitted: ${qpos.length} values but model has ${initQpos.length} DOF.`
        );
      }
      return;
    }
    const name = reserveUniqueName(rawName, rawName || "keyframe", usedKeyframeNames);
    const detail = getKeyframeDetail(rawName);
    let keyLine = `    <key name="${xmlEscapeAttr(name)}" qpos="${arrayToString(qpos)}"`;
    const appendNumericAttr = (attrName: "ctrl" | "mpos" | "mquat" | "act") => {
      const values = detail?.[attrName];
      if (!Array.isArray(values) || values.length === 0) return;
      const nums = values.map((value) => Number(value));
      if (nums.some((value) => !Number.isFinite(value))) return;
      keyLine += ` ${attrName}="${arrayToString(nums)}"`;
    };
    appendNumericAttr("ctrl");
    appendNumericAttr("mpos");
    appendNumericAttr("mquat");
    appendNumericAttr("act");
    keyLine += "/>\n";
    keyframeLines.push(keyLine);
  };

  if (initQpos.length > 0) {
    appendKeyframe("init", initQpos);
    Object.entries(storedKeyframes || {}).forEach(([keyframeName, qpos]) => {
      if (keyframeName === "init") return;
      appendKeyframe(keyframeName, qpos, { warnOnLengthMismatch: true });
    });
  }

  if (keyframeLines.length === 0) return "";
  return `  <keyframe>\n${keyframeLines.join("")}  </keyframe>\n\n`;
};

export const buildInitKeyframeQpos = ({
  bodyTreeForExport,
  jointMap,
  keyframeBodies,
  objectBodyUuidsSet,
  initKeyframeQpos,
  startupXmlFlags,
  getLiveFreeJointBodyPose,
  isScalarJoint,
  normalizeQuat,
  sanitizeMjcfName,
  toMjcfQuatWxyz,
}: {
  bodyTreeForExport: ExportBodyNode[];
  jointMap: Record<string, Joint>;
  keyframeBodies: Record<string, { position?: unknown; orientation?: unknown } | undefined>;
  objectBodyUuidsSet: Set<string>;
  initKeyframeQpos: number[] | null;
  startupXmlFlags: { seedScalarInitQposFromCurrent: boolean };
  getLiveFreeJointBodyPose: (
    body: ExportBodyNode
  ) => { position: number[]; orientation: number[] } | null;
  isScalarJoint: (joint: Joint) => joint is HingeJoint | SlideJoint;
  normalizeQuat: (values: unknown) => number[];
  sanitizeMjcfName: (value: string) => string;
  toMjcfQuatWxyz: (values: unknown) => number[];
}): number[] => {
  const qposVals: number[] = [];
  const resolveJointRef = (entry: unknown): Joint | null => {
    if (!entry) return null;
    if (typeof entry === "string") {
      const byUuid = jointMap[entry];
      if (byUuid) return byUuid;
      const normalized = sanitizeMjcfName(entry);
      const found = Object.values(jointMap).find(
        (jj) => jj && (jj.name === entry || sanitizeMjcfName(jj.name) === normalized)
      );
      return found || null;
    }
    if (typeof entry !== "object") return null;
    const jointRef = entry as Partial<Joint>;
    const baseIdentifier = jointRef.uuid ?? jointRef.name;
    if (baseIdentifier) {
      const byUuid = jointMap[String(baseIdentifier)];
      if (byUuid) return { ...jointRef, ...byUuid } as Joint;
      const normalized = sanitizeMjcfName(String(baseIdentifier));
      const found = Object.values(jointMap).find(
        (jj) => jj && (jj.name === baseIdentifier || sanitizeMjcfName(jj.name) === normalized)
      );
      return found ? ({ ...jointRef, ...found } as Joint) : (jointRef as Joint);
    }
    return jointRef as Joint;
  };
  const getBallJointQuatForKeyframe = (joint: Joint): number[] => {
    const quat = joint.kind === "ball" ? joint.quat : null;
    return Array.isArray(quat) && quat.length === 4 ? normalizeQuat(quat) : [1, 0, 0, 0];
  };
  const initIndexRef = { current: 0 };
  const appendBodyQpos = (node: ExportBodyNode) => {
    const bodyJointRefs = Array.isArray(node.joints) ? node.joints : [];
    const resolvedBodyJoints = bodyJointRefs
      .map((ref) => resolveJointRef(ref))
      .filter((j): j is Joint => !!j);
    const freeJoint = resolvedBodyJoints.find((j) => j.kind === "free");
    const isObjectBody = node.uuid != null && objectBodyUuidsSet.has(String(node.uuid));
    const useInitForBody = initKeyframeQpos != null && !isObjectBody;

    if (freeJoint) {
      if (useInitForBody && initIndexRef.current + 7 <= initKeyframeQpos!.length) {
        for (let i = 0; i < 7; i++) qposVals.push(initKeyframeQpos![initIndexRef.current + i]);
        initIndexRef.current += 7;
      } else {
        const liveFreeJointPose = getLiveFreeJointBodyPose(node);
        const overrideBody = node.uuid != null ? keyframeBodies[node.uuid as BodyUUID] : undefined;
        const p = liveFreeJointPose
          ? liveFreeJointPose.position
          : Array.isArray(overrideBody?.position) && overrideBody.position.length === 3
            ? overrideBody.position.map(Number)
            : Array.isArray(node.position) && node.position.length === 3
              ? node.position.map(Number)
              : [0, 0, 0];
        const qraw = liveFreeJointPose
          ? liveFreeJointPose.orientation
          : Array.isArray(overrideBody?.orientation) && overrideBody.orientation.length === 4
            ? overrideBody.orientation
            : Array.isArray(node.orientation) && node.orientation.length === 4
              ? node.orientation
              : [1, 0, 0, 0];
        const q = toMjcfQuatWxyz(qraw);
        qposVals.push(p[0], p[1], p[2], q[0], q[1], q[2], q[3]);
        initIndexRef.current += 7;
      }
    }
    resolvedBodyJoints.forEach((j) => {
      const kind = String(j.kind || "").toLowerCase();
      if (kind === "free") return;
      if (kind === "ball") {
        if (useInitForBody && initIndexRef.current + 4 <= initKeyframeQpos!.length) {
          for (let i = 0; i < 4; i++) qposVals.push(initKeyframeQpos![initIndexRef.current + i]);
          initIndexRef.current += 4;
        } else if (startupXmlFlags.seedScalarInitQposFromCurrent) {
          qposVals.push(...getBallJointQuatForKeyframe(j));
          initIndexRef.current += 4;
        } else {
          qposVals.push(1, 0, 0, 0);
          initIndexRef.current += 4;
        }
        return;
      }
      if (isScalarJoint(j)) {
        if (useInitForBody && initIndexRef.current < initKeyframeQpos!.length) {
          qposVals.push(initKeyframeQpos![initIndexRef.current]);
          initIndexRef.current += 1;
        } else if (startupXmlFlags.seedScalarInitQposFromCurrent) {
          // j is already the UUID-resolved joint object; use its ref directly
          // so multi-robot scenes with identical joint names don't cross-contaminate.
          const value = Number.isFinite(Number(j.ref)) ? Number(j.ref) : 0;
          qposVals.push(value);
          initIndexRef.current += 1;
        } else {
          qposVals.push(0);
          initIndexRef.current += 1;
        }
      }
    });
    node.children?.forEach(appendBodyQpos);
  };
  bodyTreeForExport.forEach(appendBodyQpos);
  return qposVals;
};
